import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { UserPlus, Building2, MapPin, Mail, Phone, User, Lock } from "lucide-react";

const INITIAL = {
  ragione_sociale: "",
  partita_iva: "",
  citta: "",
  via: "",
  email: "",
  telefono: "",
  referente_nome: "",
  referente_cognome: "",
  password: "",
  password2: "",
  privacy: false,
};

export default function ClientRegister() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const next = params.get("next") || "/area-cliente";
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) {
      toast.error("Le due password non coincidono");
      return;
    }
    if (form.password.length < 8) {
      toast.error("La password deve avere almeno 8 caratteri");
      return;
    }
    if (!form.privacy) {
      toast.error("Devi accettare l'informativa privacy");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.password2;
      delete payload.privacy;
      await api.post("/auth/register", payload);
      await refresh();
      toast.success("Registrazione completata. Benvenuto!");
      nav(next);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand/10 text-brand rounded-full text-[11px] font-display uppercase tracking-[0.2em]">
          <UserPlus className="w-3 h-3" /> Registrazione azienda
        </div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl text-ink">
          Registra la <span className="text-brand">tua azienda</span>
        </h1>
        <p className="mt-3 text-sm text-ink-muted max-w-xl mx-auto">
          Servono i dati aziendali per aprire il dossier documenti dell'iperammortamento e
          permettere al nostro team di seguirti nella pratica.
        </p>
      </div>

      <form
        onSubmit={submit}
        data-testid="register-form"
        className="mt-10 bg-white border border-line rounded-lg shadow-card p-6 md:p-8 space-y-8"
      >
        {/* Dati azienda */}
        <section>
          <SectionTitle icon={Building2} title="Dati azienda" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Field
              label="Ragione sociale"
              value={form.ragione_sociale}
              onChange={(v) => upd("ragione_sociale", v)}
              testid="reg-ragione"
              required
              placeholder="Es. Rossi S.r.l."
            />
            <Field
              label="Partita IVA / C.F."
              value={form.partita_iva}
              onChange={(v) => upd("partita_iva", v)}
              testid="reg-piva"
              required
              placeholder="IT01234567890"
            />
          </div>
        </section>

        {/* Sede */}
        <section>
          <SectionTitle icon={MapPin} title="Sede legale" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="md:col-span-2">
              <Field
                label="Via / Indirizzo"
                value={form.via}
                onChange={(v) => upd("via", v)}
                testid="reg-via"
                required
                placeholder="Via Roma 12"
              />
            </div>
            <Field
              label="Città"
              value={form.citta}
              onChange={(v) => upd("citta", v)}
              testid="reg-citta"
              required
              placeholder="Verona"
            />
          </div>
        </section>

        {/* Referente */}
        <section>
          <SectionTitle icon={User} title="Referente" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Field
              label="Nome"
              value={form.referente_nome}
              onChange={(v) => upd("referente_nome", v)}
              testid="reg-nome"
              required
            />
            <Field
              label="Cognome"
              value={form.referente_cognome}
              onChange={(v) => upd("referente_cognome", v)}
              testid="reg-cognome"
              required
            />
          </div>
        </section>

        {/* Contatti */}
        <section>
          <SectionTitle icon={Mail} title="Contatti" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Field
              label="Email"
              type="email"
              value={form.email}
              onChange={(v) => upd("email", v)}
              testid="reg-email"
              required
              autoComplete="email"
              icon={Mail}
            />
            <Field
              label="Telefono"
              type="tel"
              value={form.telefono}
              onChange={(v) => upd("telefono", v)}
              testid="reg-telefono"
              required
              placeholder="+39 045 1234567"
              icon={Phone}
            />
          </div>
        </section>

        {/* Password */}
        <section>
          <SectionTitle icon={Lock} title="Password" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Field
              label="Password (min 8 caratteri)"
              type="password"
              value={form.password}
              onChange={(v) => upd("password", v)}
              testid="reg-password"
              required
              autoComplete="new-password"
            />
            <Field
              label="Conferma password"
              type="password"
              value={form.password2}
              onChange={(v) => upd("password2", v)}
              testid="reg-password2"
              required
              autoComplete="new-password"
            />
          </div>
        </section>

        {/* Privacy */}
        <label className="flex items-start gap-3 cursor-pointer" data-testid="reg-privacy-label">
          <input
            type="checkbox"
            checked={form.privacy}
            onChange={(e) => upd("privacy", e.target.checked)}
            data-testid="reg-privacy"
            className="mt-1 w-4 h-4 accent-brand"
            required
          />
          <span className="text-xs text-ink-muted leading-relaxed">
            Ho letto e accetto l'
            <a
              href="https://www.studioingegneriavalore.it/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:underline"
            >
              informativa privacy
            </a>{" "}
            e autorizzo il trattamento dei dati per la gestione della pratica di iperammortamento.
          </span>
        </label>

        <button
          type="submit"
          disabled={loading}
          data-testid="register-submit"
          className="w-full h-12 bg-brand text-white font-display uppercase tracking-widest text-sm rounded-md hover:bg-brand-dark hover:shadow-brand-glow disabled:opacity-60 flex items-center justify-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          {loading ? "Registrazione..." : "Crea account azienda"}
        </button>

        <p className="text-sm text-ink-muted text-center">
          Hai già un account?{" "}
          <Link to={`/login${next && next !== "/area-cliente" ? `?next=${encodeURIComponent(next)}` : ""}`} data-testid="link-login" className="text-brand hover:underline">
            Accedi
          </Link>
        </p>
      </form>
    </div>
  );
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 pb-3 border-b border-line">
      <Icon className="w-4 h-4 text-brand" />
      <h2 className="font-display text-xs tracking-[0.25em] uppercase text-ink">{title}</h2>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, placeholder, autoComplete }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
        {label} {required && <span className="text-brand">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        data-testid={testid}
        className="w-full h-11 px-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
      />
    </label>
  );
}
